import { DateTime, Duration } from 'luxon';
import { Product, ProductInventoryUnit } from '../product/product';
import { OrderReturnStatus, OrderStatus, OrderType } from '../order/order';
import { WorkOrderStatus } from '../service-and-repair/service-and-repair';

export class DateTimeInterval {
  start?: DateTime;
  end?: DateTime;

  constructor(start?: DateTime, end?: DateTime) {
    this.start = start;
    this.end = end;
  }

  contains(o: DateTime): boolean {
    const startsBefore = !this.start || this.start.diff(o).milliseconds <= 0;
    const endsAfter = !this.end || this.end.diff(o).milliseconds >= 0;
    return startsBefore && endsAfter;
  }
}

export class QuantityAvailability {
  quantity!: number;
  stock?: number;
  dateTimeInterval!: DateTimeInterval;
}

export class ProductQuantityAvailability {
  product!: Product;
  availability!: QuantityAvailability[];
}

export class OrderScheduleEntry {
  readonly type!: 'Order';
  readonly locationId!: number;
  readonly location!: string;
  readonly orderNumber!: string;
  readonly orderId!: number;
  readonly orderProductId!: number;
  readonly orderStatus!: OrderStatus;
  readonly orderReturnStatus!: OrderReturnStatus;
  readonly renterName!: string;
  readonly from!: DateTime;
  readonly to?: DateTime;
  readonly bufferBefore!: Duration;
  readonly bufferAfter!: Duration;
}

export class NotInServiceScheduleEntry {
  readonly type!: 'NotInService';
  readonly from!: DateTime;
  readonly to!: DateTime;
}

export class SoldScheduleEntry {
  readonly type!: 'Sold';
  readonly from!: DateTime;
  readonly to?: DateTime;
}

export class WorkOrderScheduleEntry {
  readonly type!: 'WorkOrder';
  readonly workOrderId!: string;
  readonly workOrderStatus!: WorkOrderStatus;
  readonly from!: DateTime;
  readonly to?: DateTime;
}

export type ScheduleEntry = NotInServiceScheduleEntry | OrderScheduleEntry | SoldScheduleEntry | WorkOrderScheduleEntry;

export class UnitSchedule {
  readonly unit!: ProductInventoryUnit;
  readonly schedule!: ScheduleEntry[];
}

export class ProductSchedule {
  readonly product!: Product;
  readonly unitSchedules!: UnitSchedule[];
  readonly unassignedOrders!: OrderScheduleEntry[];
  readonly utilizationThresholdHigh!: number;
  readonly utilizationThresholdLow!: number;
}

export class UnitUtilization {
  readonly unit!: ProductInventoryUnit;
  readonly algorithm!: string;
  readonly utilization!: number;
  readonly utilizationThresholdHigh!: number;
  readonly utilizationThresholdLow!: number;
}

export class ProductUtilization {
  readonly product!: Product;
  readonly utilization!: number;
  readonly algorithm!: string;
  readonly utilizationThresholdHigh!: number;
  readonly utilizationThresholdLow!: number;
  readonly unitUtilization!: UnitUtilization[];
}

export class AssignableUnit {
  unit!: ProductInventoryUnit;
  status!: 'Available' | 'Scheduled' | 'Past Due' | 'On Rent' | 'Hard Down' | 'Sold' | 'Not In Service';
}

export class Utilization {
  readonly algorithm!: string;
  readonly utilization!: number;
  readonly utilizationThresholdHigh!: number;
  readonly utilizationThresholdLow!: number;
}

export class UnitCalendar {
  readonly unit!: ProductInventoryUnit;
  readonly utilization?: Utilization;
  readonly schedule!: ScheduleEntry[];
}

export class ProductCalendar {
  readonly product!: Product;
  readonly quantityAvailability!: QuantityAvailability[];
  readonly utilization?: Utilization;
  readonly unassignedOrders!: OrderScheduleEntry[];
  readonly unitCalendar!: UnitCalendar[];
}

export type ConfirmRentalAvailability = {
  productId: number;
  locationId: number;
  quantity: number;
  type: OrderType.Rental;
  start: DateTime;
  end?: DateTime;
};

export type ConfirmSaleAvailability = {
  productId: number;
  locationId: number;
  quantity: number;
  type: OrderType.Sale;
};

export type ConfirmAvailability = ConfirmRentalAvailability | ConfirmSaleAvailability;
